// ./src/components/generic/LoadingOverlay/LoadingOverlay.tsx
import React from "react";
import { Overlay, Center, Stack, Text, Box } from "@mantine/core";
import { LoadingIndicator, LoadingIndicatorProps } from "../LoadingIndicator";
import { Logo } from "../Logo";

// ============================================
// Types
// ============================================

export interface LoadingOverlayProps {
  /** Whether the overlay is visible */
  visible: boolean;
  /** Optional text to display below the loading indicator */
  loadingText?: string;
  /** Blur amount for the backdrop */
  blur?: number;
  /** Z-index for the overlay */
  zIndex?: number;
  /** Props for the loading indicator */
  loaderProps?: Partial<LoadingIndicatorProps>;
  /** Show app version */
  showVersion?: boolean;
  /** Show logo */
  showLogo?: boolean;
}

// ============================================
// Component
// ============================================

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  visible,
  loadingText,
  blur = 2,
  zIndex = 200,
  loaderProps = {},
  showVersion = true,
  showLogo = true,
}) => {
  if (!visible) {
    return null;
  }

  return (
    <Overlay
      color="var(--mantine-color-white)"
      backgroundOpacity={0.85}
      blur={blur}
      zIndex={zIndex}
      fixed
    >
      <Center h="100%">
        <Stack align="center" gap="lg">
          {showLogo && <Logo size="large" />}

          <Box>
            <LoadingIndicator size="lg" color="brand" {...loaderProps} />
          </Box>

          {loadingText && (
            <Text size="md" c="neutral.6" ta="center" fw={500}>
              {loadingText}
            </Text>
          )}

          {showVersion && (
            <Text
              size="xs"
              c="neutral.4"
              ta="center"
              style={{ letterSpacing: "0.02em" }}
            >
              v{import.meta.env.VITE_APP_VERSION}
            </Text>
          )}
        </Stack>
      </Center>
    </Overlay>
  );
};
